/**
 * Product Reviews Widget
 *
 * Renders product reviews with sorting, filtering, and pagination.
 * Data is fetched from the provider module via the importmap.
 */

import { fetchReviews, fetchBottomLine, hydrateTopTopics, createReview } from '@stardust/yotpo';
import { escapeHtml } from '@stardust/utilities';
// Side-effect import: registers the mobile bottom-sheet shell (assets/review-drawer.js)
// on [data-review-drawer]. Opened/closed via REVIEW_DRAWER_EVENTS below, not called directly.
import '@stardust/review-drawer';

/** Mirrors the event names review-drawer.js listens for (see that file's own copy of this constant). */
const REVIEW_DRAWER_EVENTS = {
  open: 'review-drawer:open',
  close: 'review-drawer:close'
};

/** Tailwind `md` breakpoint — matches the desktop-dialog / mobile-drawer split used across the theme. */
const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

/** How long the success message stays visible before the write-review shell auto-closes. */
const REVIEW_SUCCESS_CLOSE_DELAY_MS = 2000;

/** Star fill color — reads the section's "Star Color" setting via CSS custom property. */
const STAR_COLOR_CLASS = 'text-[var(--reviews-star-color)]';

/** id of the full dynamic-product-reviews section — the pdp-reviews block's summary scrolls here when clicked. */
const REVIEWS_WIDGET_ANCHOR_ID = 'yotpo-reviews-main-widget';

/** Review body length past which it's clamped behind a "Read more" toggle. */
const REVIEW_BODY_CLAMP_CHARS = 300;

// ─── UI Utilities ────────────────────────────────────────────────────────────

/**
 * Render a star rating as HTML
 * @param {number} rating - Rating value (0-5, supports decimals)
 * @param {Object} options
 * @param {string} [options.size='w-4 h-4'] - Tailwind size classes
 * @param {string} [options.filledColor='text-yellow-400'] - Color for filled stars
 * @param {string} [options.emptyColor='text-gray-300'] - Color for empty stars
 * @returns {string} HTML string of star SVGs
 */
export function renderStars(rating, options = {}) {
  const {
    size = 'w-4 h-4',
    filledColor = 'text-yellow-400',
    emptyColor = 'text-gray-300'
  } = options;

  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  const starPath = 'M13.849 4.22c-.684-1.626-3.014-1.626-3.698 0L8.397 8.387l-4.552.361c-1.775.14-2.495 2.331-1.142 3.477l3.468 2.937-1.06 4.392c-.413 1.713 1.472 3.067 2.992 2.149L12 19.35l3.897 2.354c1.52.918 3.405-.436 2.992-2.15l-1.06-4.39 3.468-2.938c1.353-1.146.633-3.336-1.142-3.477l-4.552-.36-1.754-4.17Z';

  const starSvg = (color) => `
    <svg class="${size} ${color}" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
      <path d="${starPath}" />
    </svg>
  `;

  const halfStarSvg = `
    <svg class="${size}" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <defs>
        <linearGradient id="half-star-grad">
          <stop offset="50%" class="${filledColor}" stop-color="currentColor" />
          <stop offset="50%" class="${emptyColor}" stop-color="currentColor" />
        </linearGradient>
      </defs>
      <path fill="url(#half-star-grad)" d="${starPath}" />
    </svg>
  `;

  let html = '';
  for (let i = 0; i < fullStars; i++) html += starSvg(filledColor);
  if (hasHalfStar) html += halfStarSvg;
  for (let i = 0; i < emptyStars; i++) html += starSvg(emptyColor);

  return html;
}

/**
 * Format a date string for display
 * @param {string} dateString - ISO date string
 * @param {string} [locale='en-US'] - Locale for formatting
 * @returns {string} Formatted date string
 */
function formatReviewDate(dateString, locale = 'en-US') {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Calculate star distribution percentages from a page of reviews.
 * Used as a fallback when the full distribution is not available.
 * @param {Array} reviews - Array of review objects with score property
 * @returns {Object} Object with keys 1-5 containing count and percentage
 */
function calculateStarDistribution(reviews) {
  const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

  reviews.forEach(review => {
    const score = Math.round(review.score);
    if (score >= 1 && score <= 5) distribution[score]++;
  });

  const total = reviews.length || 1;

  return Object.fromEntries(
    Object.entries(distribution).map(([star, count]) => [
      star, { count, percentage: Math.round((count / total) * 100) }
    ])
  );
}

/**
 * Compute a weighted average rating from a full star distribution.
 * More accurate than averaging a single page of reviews.
 * @param {{ 1: number, 2: number, 3: number, 4: number, 5: number }} dist
 * @returns {number}
 */
function weightedAverageFromDistribution(dist) {
  const total = [1, 2, 3, 4, 5].reduce((sum, s) => sum + dist[s], 0);
  if (total === 0) return 0;
  return [1, 2, 3, 4, 5].reduce((sum, s) => sum + s * dist[s], 0) / total;
}

export default class ProductReviews {
  constructor(container, { productId }) {
    this.container = container;
    this.productId = productId;

    // Cache DOM
    this.summaryEl = container.querySelector('[data-reviews-summary]');
    this.summaryCardEl = container.querySelector('[data-reviews-summary-card]');
    this.avgScoreEl = container.querySelector('[data-average-score]');
    this.starsEl = container.querySelector('[data-stars]');
    this.totalReviewsEls = container.querySelectorAll('[data-total-reviews]');
    this.summaryTextEl = container.querySelector('[data-summary-text]');
    this.distributionEl = container.querySelector('[data-reviews-distribution]');
    this.distributionBars = Object.fromEntries(
      [1, 2, 3, 4, 5].map(star => [star, {
        bar: container.querySelector(`[data-star-bar="${star}"]`),
        count: container.querySelector(`[data-star-count="${star}"]`),
      }])
    );
    this.listEl = container.querySelector('[data-reviews-list]');
    this.paginationEl = container.querySelector('[data-reviews-pagination]');
    this.sortEl = container.querySelector('[data-reviews-sort]');
    this.filterEl = container.querySelector('[data-reviews-filter]');
    this.emptyStateEl = container.querySelector('[data-reviews-empty]');

    // Write-review shells live outside the section (siblings in the section
    // file), so they're queried from the document, not the container.
    this.reviewFormTemplate = document.querySelector('[data-review-form-template]');
    this.reviewDialogEl = document.querySelector('[data-review-dialog]');
    this.reviewDrawerEl = document.querySelector('[data-review-drawer]');
    this.bindReviewShellChrome();

    // State
    this.reviews = [];
    this.starDistribution = null;
    this.totalReviewCount = 0;
    this.currentPage = 1;
    this.perPage = parseInt(container.dataset.reviewsPerPage) || 10;
    this.totalPages = 1;
    this.currentSort = 'date';
    this.currentDirection = 'desc';
    this.currentStarFilter = null;
    // Bumped at the start of every init()/fetchAndRender() call so a slower
    // response for a since-superseded load (e.g. the previous product on a
    // combined-listing morph, or a rapid double sort/page click) can detect
    // it's stale and bail instead of overwriting newer data.
    this._loadGeneration = 0;

    this.bindEvents();
    this.bindVariantChange();
    this.init();
  }

  /**
   * Keep the container's sku/image dataset in sync with the selected variant.
   * Unlike productId/title/url, these are variant-level and never touched by
   * handleSectionMorph (that only fires on a combined-listing product swap),
   * so a plain same-product variant switch left them stuck on the page-load
   * variant — submitReview() reads them straight off the container.
   */
  bindVariantChange() {
    document.addEventListener('variant:change', (e) => {
      const variant = e.detail?.variant;
      if (!variant) return;
      if (variant.product_id != null && String(variant.product_id) !== String(this.productId)) return;

      if (variant.sku != null) this.container.dataset.productSku = variant.sku;
      const imageUrl = variant.featured_image?.src || variant.featured_media?.src;
      if (imageUrl) this.container.dataset.productImage = imageUrl;
    });
  }

  /**
   * Re-sync productId from the (morphed-in-place) container and reload the
   * widget for it. Called by the shared theme:section:morph listener at the
   * bottom of this file. No-ops when the product didn't actually change, so
   * a same-product variant switch (which never touches data-product-id or
   * fires this event) does nothing.
   *
   * Combined-listing swaps morph this section in place — without this,
   * this.productId is only ever read once at construction, left stale: the
   * review list keeps showing the old product and a write-review submit
   * would attach to it too, even though submitReview()'s other fields
   * (sku/title/url/image) are read live off the container and would already
   * reflect the new product.
   */
  handleSectionMorph() {
    const newProductId = this.container.dataset.productId;
    if (!newProductId || newProductId === this.productId) return;

    this.productId = newProductId;
    this.currentPage = 1;
    this.currentSort = 'date';
    this.currentDirection = 'desc';
    this.currentStarFilter = null;
    // processReviewsData only overwrites these when the new payload has a
    // positive total / a distribution, so a swap onto a zero-review product
    // would otherwise keep showing the previous product's summary numbers.
    this.totalReviewCount = 0;
    this.starDistribution = null;
    if (this.sortEl) this.sortEl.value = 'date';
    this.updateFilterActiveState(this.filterEl?.querySelector('[data-star-filter="all"]'));

    this.init();
  }

  async init() {
    const generation = ++this._loadGeneration;

    // Re-runs on a product-changing morph (handleSectionMorph) — clear the
    // previous product's empty state so a swap to one with reviews doesn't
    // leave the "no reviews yet" message stuck on screen.
    if (this.emptyStateEl) this.emptyStateEl.classList.add('hidden');
    if (this.listEl) this.listEl.classList.remove('hidden');

    try {
      // Fetched separately from the reviews list: the bottomline (v3) reflects
      // grouped-product totals, while the list (v1) does not — see yotpo.js.
      // Its own total/distribution therefore take precedence over the list's.
      const [bottomLine, reviewsData] = await Promise.all([
        fetchBottomLine({ productId: this.productId }).catch(() => null),
        fetchReviews({
          productId: this.productId,
          perPage: this.perPage,
          page: this.currentPage,
          sort: this.currentSort,
          direction: this.currentDirection,
        })
      ]);

      if (generation !== this._loadGeneration) return; // superseded by a newer load

      // Fall back to the list's own total whenever bottomline didn't supply a
      // usable one — a failed fetch (bottomLine === null) or a resolved-but-
      // zero total both count, so a bottomline quirk can't zero out an
      // otherwise-accurate count from the reviews list.
      const bottomLineApplied = bottomLine ? this.applySummaryStats(bottomLine) : false;
      this.processReviewsData(reviewsData, { updateTotals: !bottomLineApplied });

      this.renderSummary();
      this.renderDistribution();
      this.renderReviews();
      this.renderPagination();
      this.clearSummaryLoadingState();

      if (this.reviews.length === 0) this.showEmptyState();
    } catch (error) {
      if (generation !== this._loadGeneration) return;
      console.error('Failed to load reviews:', error);
      this.showError('Failed to load reviews');
      this.clearSummaryLoadingState();
    }
  }

  /**
   * Clear the `aria-busy` set in Liquid on the summary/distribution
   * containers, once their content reflects a final state (success or
   * error) rather than the zero-filled loading markup. Runs after render so
   * assistive tech reads settled content instead of the "0" placeholder.
   */
  clearSummaryLoadingState() {
    this.summaryEl?.removeAttribute('aria-busy');
    this.summaryCardEl?.removeAttribute('aria-busy');
    this.distributionEl?.removeAttribute('aria-busy');
  }

  /**
   * Set totalReviewCount/starDistribution from an accurate summary source —
   * either the bottomline fetch, or (as a fallback) the review list's own
   * pagination total/distribution. Zero is a legitimate totalReviews value
   * for a brand-new product; the guard just avoids clobbering a real count
   * with a missing one, and totalReviewCount already starts at 0.
   * @param {{ totalReviews: number, starDistribution: Object|null }} stats
   * @returns {boolean} Whether a usable (positive) total was applied — callers
   *   use this to decide whether a fallback source is still needed.
   */
  applySummaryStats({ totalReviews, starDistribution }) {
    const applied = totalReviews > 0;
    if (applied) this.totalReviewCount = totalReviews;
    if (starDistribution) this.starDistribution = starDistribution;
    return applied;
  }

  processReviewsData({ reviews, pagination, starDistribution }, { updateTotals = true } = {}) {
    this.reviews = reviews;
    this.totalPages = pagination.totalPages;
    this.currentPage = pagination.currentPage;
    if (updateTotals) {
      this.applySummaryStats({ totalReviews: pagination.total, starDistribution });
    }
  }

  bindEvents() {
    // Write review (button in the summary card and the empty state both carry this attribute)
    this.container.addEventListener('click', (e) => {
      if (e.target.closest('[data-write-review]')) this.openReviewForm();
    });

    // Sort dropdown
    if (this.sortEl) {
      this.sortEl.addEventListener('change', (e) => {
        this.currentSort = e.target.value;
        this.currentPage = 1;
        this.fetchAndRender();
      });
    }

    // Star filter buttons
    if (this.filterEl) {
      this.filterEl.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-star-filter]');
        if (btn) {
          const star = btn.dataset.starFilter;
          this.currentStarFilter = star === 'all' ? null : parseInt(star);
          this.currentPage = 1;
          this.updateFilterActiveState(btn);
          this.fetchAndRender();
        }
      });
    }

    // Pagination
    if (this.paginationEl) {
      this.paginationEl.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-page]');
        if (btn) {
          const page = parseInt(btn.dataset.page);
          if (page !== this.currentPage && page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            this.fetchAndRender();
            this.scrollToTop();
          }
        }
      });
    }

    // Vote buttons + read-more toggles (delegated)
    if (this.listEl) {
      this.listEl.addEventListener('click', (e) => {
        const voteBtn = e.target.closest('[data-vote]');
        if (voteBtn) {
          const reviewId = voteBtn.closest('[data-review-id]')?.dataset.reviewId;
          const voteType = voteBtn.dataset.vote;
          if (reviewId) {
            this.handleVote(reviewId, voteType, voteBtn);
          }
          return;
        }

        const toggleBtn = e.target.closest('[data-review-body-toggle]');
        if (toggleBtn) {
          this.toggleReviewBody(toggleBtn);
        }
      });
    }
  }

  async fetchAndRender() {
    const generation = ++this._loadGeneration;
    this.listEl?.classList.add('opacity-50');
    try {
      const reviewsData = await fetchReviews({
        productId: this.productId,
        perPage: this.perPage,
        page: this.currentPage,
        sort: this.currentSort,
        direction: this.currentDirection,
        star: this.currentStarFilter,
      });

      if (generation !== this._loadGeneration) return; // superseded by a newer load

      this.processReviewsData(reviewsData, { updateTotals: false });
      this.renderReviews();
      this.renderPagination();
    } catch (error) {
      if (generation !== this._loadGeneration) return;
      console.error('Failed to fetch reviews:', error);
      this.showError('Failed to load reviews');
    } finally {
      // Always clear the dimming for THIS call's own request, even if it was
      // superseded (e.g. by init() on a combined-listing morph) — it's just
      // this call's loading affordance, not the data it guards above, so
      // nothing is lost by clearing it. Skipping this when superseded left
      // the list stuck dimmed forever, since nothing else ever removed it.
      this.listEl?.classList.remove('opacity-50');
    }
  }

  renderSummary() {
    let averageScore = 0;

    if (this.starDistribution) {
      averageScore = weightedAverageFromDistribution(this.starDistribution);
    } else if (this.reviews.length > 0) {
      averageScore = this.reviews.reduce((acc, r) => acc + r.score, 0) / this.reviews.length;
    }

    const totalReviews = this.totalReviewCount;
    // Truncate to 1 decimal without rounding (e.g. 4.85 → "4.8", not "4.9")
    const displayScore = (Math.floor(averageScore * 10) / 10).toFixed(1);

    if (this.avgScoreEl) this.avgScoreEl.textContent = displayScore;
    if (this.starsEl) this.starsEl.innerHTML = renderStars(averageScore, { size: 'w-5 h-5', filledColor: STAR_COLOR_CLASS });
    if (this.summaryTextEl) this.summaryTextEl.textContent = `${displayScore} out of 5`;
    this.totalReviewsEls.forEach(el => {
      el.textContent = `${totalReviews} ${totalReviews === 1 ? 'Review' : 'Reviews'}`;
    });
  }

  renderDistribution() {
    if (!this.distributionEl) return;

    let distribution;

    if (this.starDistribution) {
      const total = this.totalReviewCount || Object.values(this.starDistribution).reduce((a, b) => a + b, 0) || 1;
      distribution = Object.fromEntries(
        [1, 2, 3, 4, 5].map(star => [star, {
          count: this.starDistribution[star],
          percentage: Math.round((this.starDistribution[star] / total) * 100)
        }])
      );
    } else if (this.reviews.length) {
      distribution = calculateStarDistribution(this.reviews);
    } else {
      return;
    }

    for (let star = 5; star >= 1; star--) {
      const { bar, count } = this.distributionBars[star];
      if (bar) bar.style.width = `${distribution[star].percentage}%`;
      if (count) count.textContent = distribution[star].count;
    }
  }

  renderReviews() {
    if (!this.listEl) return;

    if (this.reviews.length === 0) {
      this.listEl.innerHTML = '';
      return;
    }

    this.listEl.innerHTML = this.reviews.map(review => this.renderReviewItem(review)).join('');
  }

  renderReviewItem(review) {
    const { id, score, author, title, content, createdAt, votesUp, votesDown, verifiedBuyer, images } = review;

    return `
      <article class="py-6 border-b border-gray-200 last:border-b-0" data-review-id="${id}">
        <div class="gap-4 xs:flex xs:items-start">
          <div class="shrink-0 space-y-2 xs:w-48 md:w-64">
            <div class="flex items-center gap-1">${renderStars(score, { filledColor: STAR_COLOR_CLASS })}</div>
            <div class="space-y-0.5">
              <p class="text-base font-semibold text-gray-900">${escapeHtml(author.displayName)}</p>
              <p class="text-sm text-gray-500">${formatReviewDate(createdAt)}</p>
            </div>
            ${verifiedBuyer ? this.renderVerifiedBadge() : ''}
          </div>
          <div class="mt-4 min-w-0 flex-1 space-y-3 xs:mt-0">
            ${title ? `<h4 class="font-semibold text-gray-900">${escapeHtml(title)}</h4>` : ''}
            ${this.renderReviewBody(content)}
            ${images?.length ? this.renderReviewImages(images) : ''}
            <div class="flex items-center gap-4 pt-2">
              <p class="text-sm text-gray-500">Was this helpful?</p>
              <button type="button" class="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors" data-vote="up">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"/>
                </svg>
                <span data-vote-count="up">${votesUp}</span>
              </button>
              <button type="button" class="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors" data-vote="down">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"/>
                </svg>
                <span data-vote-count="down">${votesDown}</span>
              </button>
            </div>
          </div>
        </div>
      </article>
    `;
  }

  renderReviewBody(content) {
    const safeContent = escapeHtml(content);

    if (content.length <= REVIEW_BODY_CLAMP_CHARS) {
      return `<p class="text-base text-gray-600">${safeContent}</p>`;
    }

    return `
      <div data-review-body>
        <p class="text-base text-gray-600 line-clamp-4" data-review-body-text>${safeContent}</p>
        <button
          type="button"
          class="mt-1 text-sm font-medium text-gray-900 hover:underline active:underline"
          data-review-body-toggle
          aria-expanded="false"
        >
          Read more
        </button>
      </div>
    `;
  }

  toggleReviewBody(toggleBtn) {
    const textEl = toggleBtn.closest('[data-review-body]')?.querySelector('[data-review-body-text]');
    if (!textEl) return;

    const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
    textEl.classList.toggle('line-clamp-4', isExpanded);
    toggleBtn.setAttribute('aria-expanded', String(!isExpanded));
    toggleBtn.textContent = isExpanded ? 'Read more' : 'Read less';
  }

  renderVerifiedBadge() {
    return `
      <div class="inline-flex items-center gap-1 mt-2">
        <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
          <path fill-rule="evenodd" d="M12 2c-.791 0-1.55.314-2.11.874l-.893.893a.985.985 0 0 1-.696.288H7.04A2.984 2.984 0 0 0 4.055 7.04v1.262a.986.986 0 0 1-.288.696l-.893.893a2.984 2.984 0 0 0 0 4.22l.893.893a.985.985 0 0 1 .288.696v1.262a2.984 2.984 0 0 0 2.984 2.984h1.262c.261 0 .512.104.696.288l.893.893a2.984 2.984 0 0 0 4.22 0l.893-.893a.985.985 0 0 1 .696-.288h1.262a2.984 2.984 0 0 0 2.984-2.984V15.7c0-.261.104-.512.288-.696l.893-.893a2.984 2.984 0 0 0 0-4.22l-.893-.893a.985.985 0 0 1-.288-.696V7.04a2.984 2.984 0 0 0-2.984-2.984h-1.262a.985.985 0 0 1-.696-.288l-.893-.893A2.984 2.984 0 0 0 12 2Zm3.683 7.73a1 1 0 1 0-1.414-1.413l-4.253 4.253-1.277-1.277a1 1 0 0 0-1.415 1.414l1.985 1.984a1 1 0 0 0 1.414 0l4.96-4.96Z" clip-rule="evenodd"/>
        </svg>
        <span class="text-sm font-medium text-green-600">Verified purchase</span>
      </div>
    `;
  }

  renderReviewImages(images) {
    return `
      <div class="flex gap-2 mt-4">
        ${images.slice(0, 4).map(img => `
          <img
            src="${escapeHtml(img.thumb_url || img.original_url)}"
            alt="Review image"
            class="h-20 w-20 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
            loading="lazy"
          />
        `).join('')}
      </div>
    `;
  }

  renderPagination() {
    if (!this.paginationEl || this.totalPages <= 1) {
      if (this.paginationEl) this.paginationEl.innerHTML = '';
      return;
    }

    this.paginationEl.innerHTML = `
      <nav
        class="flex items-center justify-between gap-2 rounded-full bg-gray-100 px-2 py-2"
        aria-label="Reviews pagination"
      >
        <button
          type="button"
          class="rounded-full px-4 py-2 text-sm hover:bg-gray-200 active:bg-gray-200 disabled:opacity-40 disabled:hover:bg-transparent"
          data-page="${this.currentPage - 1}"
          ${this.currentPage === 1 ? 'disabled' : ''}
        >
          Previous
        </button>

        <span class="text-sm text-gray-600">Page ${this.currentPage} of ${this.totalPages}</span>

        <button
          type="button"
          class="rounded-full px-4 py-2 text-sm hover:bg-gray-200 active:bg-gray-200 disabled:opacity-40 disabled:hover:bg-transparent"
          data-page="${this.currentPage + 1}"
          ${this.currentPage === this.totalPages ? 'disabled' : ''}
        >
          Next
        </button>
      </nav>
    `;
  }

  async handleVote(reviewId, voteType, buttonEl) {
    const countEl = buttonEl.querySelector(`[data-vote-count="${voteType}"]`);
    if (countEl) countEl.textContent = parseInt(countEl.textContent) + 1;
    buttonEl.disabled = true;
    buttonEl.classList.add('opacity-50');
  }

  updateFilterActiveState(activeBtn) {
    if (!this.filterEl) return;

    // Selected look comes from .button-secondary[aria-pressed="true"] (button.css).
    this.filterEl.querySelectorAll('[data-star-filter]').forEach(btn => {
      btn.setAttribute('aria-pressed', String(btn === activeBtn));
    });
  }

  showEmptyState() {
    if (this.emptyStateEl) {
      this.emptyStateEl.classList.remove('hidden');
    }
    if (this.listEl) {
      this.listEl.classList.add('hidden');
    }
  }

  showError(message) {
    if (this.listEl) {
      this.listEl.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          <p>${escapeHtml(message)}</p>
        </div>
      `;
    }
  }

  scrollToTop() {
    this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ─── Write a review ────────────────────────────────────────────────────

  /**
   * Bind the write-review shells' permanent chrome (header close button on
   * both, backdrop-click-to-close on the dialog) once at construction. This
   * markup is never re-cloned — only the form inside [data-review-form-slot]
   * is — so binding it per-open (as openReviewForm used to) stacked a
   * duplicate handler on every open.
   */
  bindReviewShellChrome() {
    [this.reviewDialogEl, this.reviewDrawerEl].forEach((shell) => {
      shell?.querySelector('[data-close-modal]')?.addEventListener('click', () => this.closeReviewForm(shell));
    });

    this.reviewDialogEl?.addEventListener('click', (e) => {
      if (e.target === this.reviewDialogEl) this.closeReviewForm(this.reviewDialogEl);
    });
  }

  /**
   * Open the write-review form: a native <dialog> on desktop, a bottom-sheet
   * drawer on mobile (assets/review-drawer.js). Clones the shared form
   * template into whichever shell matches the current viewport.
   */
  openReviewForm() {
    if (!this.reviewFormTemplate) return;

    // A previous submission's auto-close is still pending — cancel it so it
    // can't fire against this freshly (re)opened form a moment later.
    clearTimeout(this._reviewSuccessTimeoutId);

    const isDesktop = window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
    const shell = isDesktop ? this.reviewDialogEl : this.reviewDrawerEl;
    if (!shell) return;

    // Clear every shell's slot, not just the one we're about to open into —
    // otherwise a clone left in the other shell from before a desktop/mobile
    // breakpoint change keeps its review-*-{{ section.id }} ids in the
    // document, colliding with the fresh clone below.
    [this.reviewDialogEl, this.reviewDrawerEl].forEach((s) => {
      const otherSlot = s?.querySelector('[data-review-form-slot]');
      if (otherSlot) otherSlot.innerHTML = '';
    });

    const slot = shell.querySelector('[data-review-form-slot]');
    slot.appendChild(this.reviewFormTemplate.content.cloneNode(true));

    const form = slot.querySelector('[data-review-form]');
    this.wireReviewForm(shell, form);
    form.querySelector('[data-close-modal]')?.addEventListener('click', () => this.closeReviewForm(shell));

    if (isDesktop) {
      shell.showModal();
    } else {
      document.dispatchEvent(new CustomEvent(REVIEW_DRAWER_EVENTS.open));
    }
  }

  /**
   * Close whichever write-review shell is open.
   * @param {HTMLElement} shell
   */
  closeReviewForm(shell) {
    clearTimeout(this._reviewSuccessTimeoutId);
    if (shell.tagName === 'DIALOG') {
      shell.close();
    } else {
      document.dispatchEvent(new CustomEvent(REVIEW_DRAWER_EVENTS.close));
    }
  }

  /**
   * Wire star-rating selection and submit handling for a freshly-cloned review form.
   * @param {HTMLElement} shell
   * @param {HTMLFormElement} form
   */
  wireReviewForm(shell, form) {
    const ratingButtons = form.querySelectorAll('[data-rating]');
    const ratingInput = form.querySelector('[data-rating-input]');

    ratingButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = parseInt(btn.dataset.rating, 10);
        ratingInput.value = value;
        // Radiogroup semantics: exactly one star is "checked" — its selected
        // look comes from .button-secondary[aria-checked="true"] (button.css).
        ratingButtons.forEach((b) => {
          b.setAttribute('aria-checked', String(parseInt(b.dataset.rating, 10) === value));
        });
      });
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitReview(shell, form);
    });
  }

  /**
   * Submit the write-review form via the Yotpo provider's createReview().
   * @param {HTMLElement} shell
   * @param {HTMLFormElement} form
   */
  async submitReview(shell, form) {
    const errorEl = form.querySelector('[data-review-form-error]');
    const submitBtn = form.querySelector('[data-review-submit]');
    errorEl.classList.add('hidden');

    const data = new FormData(form);
    const rating = parseInt(data.get('rating'), 10);
    if (!rating) {
      errorEl.textContent = 'Please select a star rating.';
      errorEl.classList.remove('hidden');
      return;
    }

    // The rating is a hidden input, which the constraint validation API
    // ignores entirely (hidden inputs are barred from constraint
    // validation), hence the manual check above. Every other field is a
    // real control, so the form's `novalidate` (added to suppress the
    // browser's automatic validation-on-submit, which would run before our
    // rating check above) doesn't block calling the API directly —
    // reportValidity() still shows the native inline message on the first
    // invalid field.
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    submitBtn.disabled = true;
    submitBtn.classList.add('opacity-50');

    try {
      await createReview({
        productId: this.productId,
        sku: this.container.dataset.productSku,
        productTitle: this.container.dataset.productTitle,
        productUrl: this.container.dataset.productUrl,
        productImageUrl: this.container.dataset.productImage,
        score: rating,
        title: data.get('title'),
        content: data.get('content'),
        displayName: data.get('display_name'),
        email: data.get('email'),
      });

      form.innerHTML = '<p class="py-8 text-center text-gray-700">Thanks! Your review has been submitted and will appear after moderation.</p>';
      this._reviewSuccessTimeoutId = setTimeout(() => this.closeReviewForm(shell), REVIEW_SUCCESS_CLOSE_DELAY_MS);
    } catch (error) {
      console.error('Failed to submit review:', error);
      errorEl.textContent = error.message || 'Something went wrong submitting your review. Please try again.';
      errorEl.classList.remove('hidden');
      submitBtn.disabled = false;
      submitBtn.classList.remove('opacity-50');
    }
  }
}

// ─── PDP Reviews Summary (stars + count + topics strip) ─────────────────────

/**
 * Paint the bottom-line summary (stars, average, review count) into a
 * [data-pdp-reviews] container. Split out of hydratePdpReviews so
 * initPdpReviewsBlock can repaint from a cached fetch without hitting the
 * network again — see the cache-hit branch there for why that's needed.
 * @param {HTMLElement} el
 * @param {{ averageScore: number, totalReviews: number }} bottomLine
 */
function paintPdpReviewsSummary(el, { averageScore, totalReviews }) {
  // Reuse the same selectors as ProductReviews.renderSummary()
  const displayScore = (Math.floor(averageScore * 10) / 10).toFixed(1);

  const starsEl = el.querySelector('[data-stars]');
  if (starsEl) {
    starsEl.innerHTML = renderStars(averageScore, { size: 'w-5 h-5', filledColor: STAR_COLOR_CLASS });
  }

  const avgScoreEl = el.querySelector('[data-average-score]');
  if (avgScoreEl) avgScoreEl.textContent = displayScore;

  el.querySelectorAll('[data-total-reviews]').forEach((countEl) => {
    countEl.textContent = `${totalReviews} ${totalReviews === 1 ? 'Review' : 'Reviews'}`;
  });

  el.removeAttribute('aria-busy');
}

/**
 * Hydrate a single [data-pdp-reviews] container with the Yotpo bottom-line
 * summary and top-mentioned topics, and cache the result on the element so a
 * later same-product re-morph can repaint without re-fetching (see
 * initPdpReviewsBlock).
 * @param {HTMLElement} el
 */
async function hydratePdpReviews(el) {
  const productId = el.dataset.productId;
  if (!productId) return;

  const showTopics = el.dataset.showTopics !== 'false';
  const topicsCount = parseInt(el.dataset.topicsCount, 10) || 3;

  // Bottom line — fetched independently so a topics failure cannot suppress it
  try {
    const bottomLine = await fetchBottomLine({ productId });
    // A rapid combined-listing morph can swap el.dataset.productId to a
    // different product while this fetch is in flight — bail so a slower
    // response for the old product can't paint over the new one.
    if (el.dataset.productId !== productId) return;
    paintPdpReviewsSummary(el, bottomLine);
    el._pdpReviewsCache = { productId, bottomLine, topicsHtml: '' };
  } catch (err) {
    console.warn('[pdp-reviews] Failed to load bottom line:', err);
    if (el.dataset.productId === productId) el.removeAttribute('aria-busy');
  }

  // Topics — Yotpo-specific; hydrateTopTopics lives in the provider module
  // because not all review platforms expose this feature.
  if (showTopics && el.dataset.productId === productId) {
    await hydrateTopTopics(el, { productId, count: topicsCount });
    if (el.dataset.productId === productId && el._pdpReviewsCache?.productId === productId) {
      el._pdpReviewsCache.topicsHtml = el.querySelector('[data-reviews-topics]')?.innerHTML ?? '';
    }
  }
}

/**
 * Hydrate a [data-pdp-reviews] block, or repaint it from a cached fetch.
 *
 * Combined-listing and same-product variant morphs both re-render this
 * block from Liquid, which always emits the SSR placeholder skeleton (0
 * stars, "0 Reviews", aria-busy) — the fetched summary is never part of the
 * server-rendered markup, so every morph wipes it regardless of whether the
 * product changed. That means the DOM must be repainted after every morph,
 * but the network fetch itself is only needed when the product actually
 * changed — a same-product variant switch has the same reviews. The cache
 * left on the element by hydratePdpReviews lets us tell those two needs
 * apart: repaint from cache on a same-product morph, fetch fresh otherwise.
 * @param {HTMLElement} el
 */
function initPdpReviewsBlock(el) {
  const productId = el.dataset.productId;
  if (!productId) return;

  const cache = el._pdpReviewsCache;
  if (cache?.productId === productId) {
    paintPdpReviewsSummary(el, cache.bottomLine);
    const topicsEl = el.querySelector('[data-reviews-topics]');
    if (topicsEl) topicsEl.innerHTML = cache.topicsHtml;
    return;
  }

  hydratePdpReviews(el);
}

// ─── Auto-initialize both widgets ────────────────────────────────────────────
// Guard against DOMContentLoaded having already fired (Shopify block injection
// can add scripts after the event fires).

/** The page's single ProductReviews instance, if the full section is present. */
let currentProductReviews = null;

function initAll() {
  const reviewsContainer = document.querySelector('[data-product-reviews]');
  if (reviewsContainer?.dataset.productId) {
    currentProductReviews = new ProductReviews(reviewsContainer, { productId: reviewsContainer.dataset.productId });
  }

  document.querySelectorAll('[data-pdp-reviews]').forEach(initPdpReviewsBlock);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

// The compact [data-pdp-reviews] summary is a jump link to the full reviews
// widget below. Delegated on document so a morphed-in-place block (see
// initPdpReviewsBlock) never needs its listener re-bound.
document.addEventListener('click', (e) => {
  if (!e.target.closest('[data-pdp-reviews] [data-reviews-summary]')) return;
  document.getElementById(REVIEWS_WIDGET_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

// Combined-listing option changes morph the product section in place
// (assets/product.js #morphSectionsForCombinedListing) — re-sync both
// widgets afterward so one that landed on a different product picks up that
// product's rating/reviews instead of showing stale data.
document.addEventListener('theme:section:morph', (event) => {
  currentProductReviews?.handleSectionMorph();
  // A combined-listing swap morphs several sections at once, each dispatching
  // its own event; scope to the morphed section so sections without a
  // [data-pdp-reviews] block don't trigger redundant document-wide re-inits.
  const root = event.target;
  const blocks = root.matches('[data-pdp-reviews]') ? [root] : root.querySelectorAll('[data-pdp-reviews]');
  blocks.forEach(initPdpReviewsBlock);
});
